import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  loadUserProfile,
  listenMarketplaceItemsByStatus,
  listenMarketplaceReviews,
  addMarketplaceReview,
  hasUserImportedMarketplaceItem,
} from "../services/data";

const isHttpUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
const getThumbUrl = (item) => {
  if (!item) return "";
  if (item.releaseUrls?.thumbUrl) return item.releaseUrls.thumbUrl;
  if (isHttpUrl(item.thumbPath)) return item.thumbPath;
  return "";
};
const getGalleryUrls = (item) =>
  Array.isArray(item?.releaseUrls?.galleryUrls)
    ? item.releaseUrls.galleryUrls.filter(Boolean)
    : [];

export default function MarketplaceTemplateDetail({
  user,
  item,
  onBack,
  onImport,
}) {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [currentItem, setCurrentItem] = useState(item || null);
  const [itemLoading, setItemLoading] = useState(false);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [ownerTemplates, setOwnerTemplates] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const currentItemId = currentItem?.id || currentItem?.itemId || null;

  // Load item from URL if itemId param exists and no item prop provided
  useEffect(() => {
    if (!itemId || item) return; // Skip if URL param missing or item already provided via props
    
    let unsubscribe = null;
    setItemLoading(true);
    
    // Listen to marketplace items and find the one with matching ID
    unsubscribe = listenMarketplaceItemsByStatus(
      { status: "published" },
      (items) => {
        const foundItem = items.find((i) => i.id === itemId);
        if (foundItem) {
          setCurrentItem(foundItem);
          setItemLoading(false);
        } else if (items.length > 0) {
          // Items loaded but none match - item not found
          setItemLoading(false);
        }
      }
    );
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [itemId, item]);

  useEffect(() => {
    if (!currentItemId) return;
    const un = listenMarketplaceReviews(currentItemId, setReviews);
    return () => un && un();
  }, [currentItemId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid || !currentItemId) {
        setCanReview(false);
        return;
      }
      const ok = await hasUserImportedMarketplaceItem(user.uid, currentItemId);
      if (alive) setCanReview(!!ok);
    })();
    return () => {
      alive = false;
    };
  }, [user?.uid, currentItemId]);

  // Récupère profil + autres templates du même owner
  useEffect(() => {
    if (!currentItem) return;

    const ownerUid =
      currentItem.ownerUid || currentItem.uid || currentItem.owner_id;
    if (!ownerUid) return;

    // Profil utilisateur
    loadUserProfile(ownerUid)
      .then((prof) => setOwnerProfile(prof || null))
      .catch((err) =>
        console.error("[detail] loadUserProfile error:", err?.message || err)
      );

    // Autres templates du même créateur (publiés)
    const un = listenMarketplaceItemsByStatus(
      { status: "published", ownerUid },
      (rows) => setOwnerTemplates(rows || [])
    );
    return () => un && un();
  }, [currentItem]);

  // Define galleryUrls early (before conditional return)
  const galleryUrls = getGalleryUrls(currentItem);

  // Lightbox keyboard handler - MUST be before any conditional returns
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightboxOpen(false);
      } else if (e.key === "ArrowRight") {
        setLightboxIndex((i) =>
          galleryUrls.length ? (i + 1) % galleryUrls.length : 0
        );
      } else if (e.key === "ArrowLeft") {
        setLightboxIndex((i) =>
          galleryUrls.length ? (i - 1 + galleryUrls.length) % galleryUrls.length : 0
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, galleryUrls.length]);


  // Lightbox body overflow handler - MUST be before any conditional returns
  useEffect(() => {
    if (lightboxOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [lightboxOpen]);

  // Early return if no item - all hooks MUST be called before this
  if (!currentItem) {
    return (
      <div className="template-detail-page">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-file-contract" /> Template indisponible
          </h1>
          <div className="quick-actions">
            <button 
              className="btn btn-outline" 
              onClick={() => {
                if (onBack) onBack();
                else navigate('/app/marketplace');
              }}
            >
              <i className="fas fa-arrow-left" /> Retour au marketplace
            </button>
          </div>
        </div>
        <p>Le template demandé n'est plus disponible.</p>
      </div>
    );
  }

  const thumbUrl = getThumbUrl(currentItem);
  const previewUrl = thumbUrl || (galleryUrls[0] || "");
  const hasPreviewImage = !!previewUrl;

  const openLightbox = (idx) => {
    if (!Array.isArray(galleryUrls) || !galleryUrls.length) return;
    setLightboxIndex(Math.max(0, Math.min(idx, galleryUrls.length - 1)));
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goPrev = () => {
    if (!galleryUrls.length) return;
    setLightboxIndex((i) => (i - 1 + galleryUrls.length) % galleryUrls.length);
  };

  const goNext = () => {
    if (!galleryUrls.length) return;
    setLightboxIndex((i) => (i + 1) % galleryUrls.length);
  };

  const isPremium = !!currentItem.isPremium;
  const priceLabel = !isPremium
    ? "Gratuit"
    : `${
        typeof currentItem.price === "number"
          ? currentItem.price
          : Number(currentItem.price || 0)
      } ${currentItem.currency || "MAD"}`;

  const ownerName =
    (ownerProfile &&
      (ownerProfile.displayName ||
        ownerProfile.fullName ||
        ownerProfile.name)) ||
    currentItem.ownerName ||
    "Créateur inconnu";

  const ownerDescription =
    (ownerProfile && (ownerProfile.bio || ownerProfile.description)) ||
    currentItem.ownerDescription ||
    "Aucune description fournie pour ce créateur.";

  const localRatingCount =
    reviews.length ||
    currentItem.ratingCount ||
    currentItem.reviewsCount ||
    null;
  const localRatingAvg =
    reviews.length > 0
      ? reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0) /
        reviews.length
      : currentItem.ratingAvg ?? currentItem.avgRating ?? null;

  const publishedCount = ownerTemplates.filter(
    (tpl) => tpl.status === "published"
  ).length;

  const ratingValue =
    localRatingAvg ??
    currentItem.avgRating ??
    ownerProfile?.ratingAvg ??
    ownerProfile?.avgRating ??
    null;

  const ratingCount =
    localRatingCount ??
    currentItem.ratingsCount ??
    ownerProfile?.ratingCount ??
    ownerProfile?.ratingsCount ??
    null;

  const otherTemplates = ownerTemplates.filter(
    (tpl) => tpl.id !== currentItem.id
  );

  const handleImportClick = async () => {
    if (!currentItem) return;
    if (!currentItem?.release?.docxPath) {
      alert("Ce template n'a pas encore de fichier disponible.");
      return;
    }
    if (!onImport) {
      alert("L'import vers vos modèles n'est pas disponible.");
      return;
    }
    try {
      setImportLoading(true);
      await Promise.resolve(onImport(currentItem));
    } catch (err) {
      console.error("[detail] Import marketplace template failed:", err);
      alert(
        "Impossible d'importer ce template pour le moment. Réessaie plus tard."
      );
    } finally {
      setImportLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!currentItemId || !user?.uid || !canReview) return;
    if (!reviewRating || reviewRating < 1 || reviewRating > 5) {
      alert("Attribue une note entre 1 et 5.");
      return;
    }
    setReviewSaving(true);
    try {
      await addMarketplaceReview({
        itemId: currentItemId,
        uid: user.uid,
        rating: Number(reviewRating),
        comment: reviewComment,
        userDisplayName:
          user.displayName || user.fullName || user.email || "Utilisateur",
      });
      setReviewComment("");
    } catch (err) {
      console.error("[detail] add review failed:", err);
      alert("Impossible d'enregistrer ton avis pour le moment.");
    } finally {
      setReviewSaving(false);
    }
  };

  const formatDate = (v) => {
    if (!v) return "";
    try {
      if (v?.toDate) return v.toDate().toLocaleDateString();
      if (typeof v.seconds === "number")
        return new Date(v.seconds * 1000).toLocaleDateString();
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch {}
    return "";
  };

  return (
    <div className="template-detail-page">
      <div className="page-header">
        <h1 className="page-title">
          <i className="fas fa-file-contract" /> Détail du template
        </h1>
        <div className="quick-actions">
          <button 
            className="btn btn-outline" 
            onClick={() => {
              if (onBack) onBack();
              else navigate('/app/marketplace');
            }}
          >
            <i className="fas fa-arrow-left" /> Retour au marketplace
          </button>
        </div>
      </div>

      <div className="template-detail-layout">
        {/* Bloc principal : preview + sidebar infos */}
        <div className="template-preview-container">
          <div className="template-preview-card">
            <div className="template-preview-header">
              <div>
                <div className="template-preview-title">
                  <i className="fas fa-file-alt" />
                  {currentItem.title || "—"}
                </div>
                {currentItem.subtitle && (
                  <div className="template-preview-subtitle">
                    {currentItem.subtitle}
                  </div>
                )}
              </div>
              <span
                className={`template-type ${
                  isPremium ? "type-premium" : "type-free"
                }`}
              >
                {isPremium ? "Premium" : "Gratuit"}
              </span>
            </div>

            <div className="template-preview-body">
              {hasPreviewImage ? (
                <div className="template-preview-image">
                  <img
                    src={previewUrl}
                    alt={`Aperçu ${currentItem.title || ""}`}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="template-preview-content template-preview-placeholder">
                  <i className="fas fa-file-invoice-dollar" />
                  <div>Prévisualisation du modèle</div>
                  <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                    (Aperçu dynamique plus avancé possible plus tard)
                  </div>
                </div>
              )}

              {galleryUrls.length > 0 && (
                <div className="template-gallery">
                  {galleryUrls.map((url, idx) => (
                    <div
                      key={`${url}-${idx}`}
                      className="template-gallery-item"
                      onClick={() => openLightbox(idx)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openLightbox(idx);
                        }
                      }}
                      style={{ cursor: "zoom-in" }}
                    >
                      <img
                        src={url}
                        alt={`Capture ${idx + 1} - ${currentItem.title || ""}`}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="template-info-sidebar">
            <div className="template-info-header">
              <div
                className={`template-price ${isPremium ? "premium" : "free"}`}
              >
                {priceLabel}
              </div>
              <div className="template-rating">
                <i className="fas fa-star" />
                {ratingValue != null ? (
                  <span>
                    {Number(ratingValue).toFixed(1)}{" "}
                    {ratingCount != null ? `(${ratingCount} avis)` : ""}
                  </span>
                ) : (
                  <span>Pas encore d’évaluation</span>
                )}
              </div>
            </div>

            <div className="template-actions">
              <button
                className="download-btn"
                disabled={importLoading || !currentItem?.release?.docxPath}
                onClick={handleImportClick}
              >
                <i className="fas fa-download" />{" "}
                {importLoading ? "Import en cours..." : "Télécharger le modèle"}
              </button>
            </div>

            <div className="template-details">
              <div className="detail-item">
                <i className="fas fa-tag detail-icon" />
                <div>
                  <div className="detail-label">Catégorie</div>
                  <div className="detail-value">
                    {currentItem.category || "—"}
                  </div>
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-language detail-icon" />
                <div>
                  <div className="detail-label">Langues</div>
                  <div className="detail-value">
                    {Array.isArray(currentItem.languages)
                      ? currentItem.languages.join(", ")
                      : "—"}
                  </div>
                </div>
              </div>
              <div className="detail-item">
                <i className="fas fa-globe detail-icon" />
                <div>
                  <div className="detail-label">Juridictions</div>
                  <div className="detail-value">
                    {Array.isArray(currentItem.jurisdictions)
                      ? currentItem.jurisdictions.join(", ")
                      : currentItem.jurisdiction || currentItem.country || "—"}
                  </div>
                </div>
              </div>
              {currentItem.updatedAt && (
                <div className="detail-item">
                  <i className="fas fa-calendar detail-icon" />
                  <div>
                    <div className="detail-label">Mis à jour</div>
                    <div className="detail-value">
                      {formatDate(currentItem.updatedAt)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {Array.isArray(currentItem.tags) && currentItem.tags.length > 0 && (
              <div className="template-tags">
                {currentItem.tags.map((tag) => (
                  <span key={tag} className="pill-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </aside>
        </div>

        {/* Description détaillée */}
        <section className="template-description-section">
          <div className="section-title">
            <i className="fas fa-align-left" />
            Description
          </div>
          <div className="template-description">
            {currentItem.longDescription ? (
              <p>{currentItem.longDescription}</p>
            ) : currentItem.description ? (
              <p>{currentItem.description}</p>
            ) : (
              <p>Aucune description détaillée n’a encore été fournie.</p>
            )}
          </div>
        </section>

        {/* Fonctionnalités */}
        <section className="template-features-section">
          <div className="section-title">
            <i className="fas fa-puzzle-piece" />
            Fonctionnalités incluses
          </div>
          <div className="features-grid">
            {Array.isArray(currentItem.features) &&
            currentItem.features.length > 0
              ? currentItem.features.map((f) => (
                  <div key={f} className="feature-item">
                    <i className="fas fa-check-circle" />
                    <span>{f}</span>
                  </div>
                ))
              : [
                  "Formules de calcul automatique",
                  "Sections répétées",
                  "Champs de signature",
                  "Support multilingue",
                  "Export PDF",
                  "Export DOCX",
                ].map((f) => (
                  <div key={f} className="feature-item">
                    <i className="fas fa-check-circle" />
                    <span>{f}</span>
                  </div>
                ))}
          </div>
        </section>

        {/* Avis / Reviews */}
        <section className="reviews-section">
          <div className="reviews-header">
            <div className="reviews-title">
              <i className="fas fa-star" />
              <span>Évaluations et commentaires</span>
            </div>
            <div className="reviews-pill">
              {ratingValue != null
                ? `${Number(ratingValue).toFixed(1)} / 5`
                : "Pas noté"}
              {ratingCount != null ? ` · ${ratingCount} avis` : ""}
            </div>
            {user?.uid && canReview && (
              <button
                type="button"
                className="add-review-btn"
                onClick={() => setShowReviewForm((v) => !v)}
              >
                <i
                  className={`fas ${showReviewForm ? "fa-times" : "fa-plus"}`}
                />
                {showReviewForm ? "Fermer" : "Ajouter une évaluation"}
              </button>
            )}
          </div>

          <div className="reviews-summary">
            <div className="average-rating">
              <div className="rating-value-large">
                {ratingValue != null ? Number(ratingValue).toFixed(1) : "–"}
              </div>
              <div className="rating-stars">
                <i className="fas fa-star" />
                <span>Moyenne</span>
              </div>
              <div className="rating-count-muted">
                {ratingCount != null
                  ? `${ratingCount} avis`
                  : "Aucun avis pour l'instant"}
              </div>
            </div>
          </div>

          <div className="reviews-list">
            {reviews.length === 0 ? (
              <div className="muted">Aucun avis pour le moment.</div>
            ) : (
              reviews.map((rev) => (
                <div key={rev.id} className="review-item">
                  <div className="review-header">
                    <div className="review-author">
                      <div className="review-avatar">
                        {(rev.userDisplayName || "U").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="review-name">
                          {rev.userDisplayName || "Utilisateur"}
                        </div>
                        <div className="review-date">
                          {rev.updatedAt ? formatDate(rev.updatedAt) : ""}
                        </div>
                      </div>
                    </div>
                    <div className="review-rating">
                      <i className="fas fa-star" /> {rev.rating || "-"}
                    </div>
                  </div>
                  {rev.comment && (
                    <div className="review-comment">{rev.comment}</div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className={`review-form ${showReviewForm ? "active" : ""}`} id="reviewForm">
            {user?.uid ? (
              canReview ? (
                <div className="review-form-body">
                  <div className="form-group">
                    <label className="form-label">Votre note</label>

                    <div
                      className="star-rating"
                      role="radiogroup"
                      aria-label="Votre note"
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`star-btn ${
                            (hoverRating || reviewRating) >= n ? "active" : ""
                          }`}
                          onMouseEnter={() => setHoverRating(n)}
                          onMouseMove={() => setHoverRating(n)}
                          onFocus={() => setHoverRating(n)}
                          onBlur={() => setHoverRating(0)}
                          onClick={() => setReviewRating(n)} // logique inchangée: clic = choix final
                          role="radio"
                          aria-checked={reviewRating === n}
                          aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                          title={`${n}/5`}
                        >
                          <i className="fas fa-star" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">
                      Commentaire (optionnel)
                    </label>
                    <textarea
                      className="form-textarea"
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Partage ton retour d'expérience"
                      rows={4}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitReview}
                      disabled={reviewSaving}
                    >
                      {reviewSaving ? "Envoi..." : "Envoyer mon avis"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="muted">
                  Importe ce template pour laisser un avis.
                </div>
              )
            ) : (
              <div className="muted">
                Connecte-toi et importe ce template pour laisser un avis.
              </div>
            )}
          </div>
        </section>

        {/* Profil du créateur */}
        <section className="owner-profile-section">
          <div className="section-title">
            <i className="fas fa-user-circle" />À propos du créateur
          </div>
          <div className="owner-profile-body">
            <div className="owner-main">
              <div className="owner-avatar">
                <span>
                  {ownerName
                    .split(" ")
                    .map((p) => p[0])
                    .filter(Boolean)
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              </div>
              <div className="owner-info">
                <div className="owner-name">{ownerName}</div>
                {ownerProfile?.title && (
                  <div className="owner-title">{ownerProfile.title}</div>
                )}
                <div className="owner-rating">
                  <div className="rating-stars">
                    <i className="fas fa-star" />
                  </div>
                  <div className="rating-value">
                    {ratingValue != null ? Number(ratingValue).toFixed(1) : "—"}
                  </div>
                  <div className="rating-count">
                    {ratingCount != null
                      ? `${ratingCount} avis`
                      : "Aucun avis pour le moment"}
                  </div>
                </div>
              </div>
            </div>
            <div className="owner-stats">
              <div className="owner-stat-item">
                <div className="stat-value">{publishedCount}</div>
                <div className="stat-label">Templates publiés</div>
              </div>
              {ownerProfile?.country && (
                <div className="owner-stat-item">
                  <div className="stat-value">{ownerProfile.country}</div>
                  <div className="stat-label">Pays</div>
                </div>
              )}
            </div>
          </div>
          <div className="owner-description">
            <p>{ownerDescription}</p>
          </div>
        </section>

        {/* Autres templates du même créateur */}
        {otherTemplates.length > 0 && (
          <section className="owner-templates-section">
            <div className="section-title">
              <i className="fas fa-layer-group" />
              Autres templates de ce créateur
            </div>
            <div className="owner-templates-grid">
              {otherTemplates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="owner-template-card"
                  onClick={() => setCurrentItem(tpl)}
                >
                  <div className="owner-template-title">
                    <i className="fas fa-file-alt" />
                    {tpl.title || "—"}
                  </div>
                  <div className="owner-template-meta">
                    <span>{tpl.category || "—"}</span>
                    <span>
                      {Array.isArray(tpl.languages)
                        ? tpl.languages.join(", ")
                        : "—"}
                    </span>
                  </div>
                  <div className="owner-template-price">
                    {!tpl.isPremium
                      ? "Gratuit"
                      : `${
                          typeof tpl.price === "number"
                            ? tpl.price
                            : Number(tpl.price || 0)
                        } ${tpl.currency || "MAD"}`}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {lightboxOpen && galleryUrls.length > 0 && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={closeLightbox}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "min(90vw, 1200px)",
              maxHeight: "90vh",
              width: "100%",
              background: "#000",
              borderRadius: 12,
              boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              aria-label="Fermer"
              onClick={closeLightbox}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              ×
            </button>

            {galleryUrls.length > 1 && (
              <button
                aria-label="Précédent"
                onClick={goPrev}
                style={{
                  position: "absolute",
                  left: 12,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                ‹
              </button>
            )}

            <img
              src={galleryUrls[lightboxIndex]}
              alt={`Capture ${lightboxIndex + 1} - ${currentItem.title || ""}`}
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                display: "block",
              }}
            />

            {galleryUrls.length > 1 && (
              <button
                aria-label="Suivant"
                onClick={goNext}
                style={{
                  position: "absolute",
                  right: 12,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
